import type { MRT_AggregationFn, MRT_AggregationOption, MRT_ColumnDef } from "mantine-react-table"
import React from "react"
import { Button } from "@mantine/core"

import ColumnActionMenu, { useStyles } from "app/static/frontend/sheets/components/ColumnActionMenu"
import { MantineGroupedSheetCell, MantineSheetCell } from "app/static/frontend/sheets/components/cells/SheetCell"

import sheetConstants from "app/static/frontend/sheets/constants"
import {
    buildColumnGlobalSearchProperties,
    buildMantineColumnFilters,
    buildMantineSortingFunctions,
    buildGetGroupingValue,
    getMathFooterLabel,
    handleRenameColumn,
    isStatColumn,
    isColumnColoringEnabled,
    getSafedSearchRowId,
} from "app/static/frontend/sheets/mantine-sheet/mantine-sheet-helper-functions"

import { fromJS } from "immutable"
import { centsToCurrencyStr } from "shared/pac/helperFunctions"
import { MantineAggregationCell } from "app/static/frontend/sheets/components/MantineAggregationCell"
import { Icon } from "@/components/Icon"

/**
 * Build the Mantine Sheet Column Definitions
 */
export const getColumnDefinition = ({
    alignmentTypes,
    canAddColumns,
    canFitInMemory,
    canHideColumns,
    canRemoveColumns,
    column,
    columnCount,
    columnIndex,
    columns,
    columnsToRender,
    dataType,
    id,
    removeColumn,
    rowData,
    sheetId,
    shouldDisplayMathBar,
    stanceOptions,
    toggleColumnIsHiddenOnPublic,
    renameColumn,
    canRenameColumns,
    isPinned,
    togglePinnedSort,
}): MRT_ColumnDef => {
    const { can_filter_on_backend: canFilterOnBackend, can_sort_on_backend: canSortOnBackend } = column
    const columnFilterData = buildMantineColumnFilters({
        columnData: column,
        canFitInMemory,
        alignmentTypes,
        dataType,
        allImmutableColumns: columns,
        canFilterOnBackend,
    })
    const columnSortData = buildMantineSortingFunctions({
        column,
        sheetDataType: dataType,
        canFitInMemory,
        canSortOnBackend,
    })

    let globalSearchProps = {}
    // @ts-expect-error - globalSearchFunction is CONDITIONALLY returned, so this COULD be undefined . . . TODO: Fix
    const { globalSearchFunction, ...rest } = buildColumnGlobalSearchProperties({
        column,
        sheetDataType: dataType,
        alignmentTypes,
        stanceOptions,
        columns,
    })
    globalSearchProps = { ...rest }
    // meta field is Quorum-specific information about the column that's not necessarily relevant to MRT
    const columnMeta = {
        columnType: column.column_type,
        columnDataType: column.column_data_type,
        searchStringGenerator: globalSearchFunction,
        isPinned: isPinned,
    }
    let columnAggregationData = {} as {
        aggregationFn?: MRT_AggregationFn
        AggregatedCell?: (props: any) => JSX.Element
    }
    let columnGroupingData = {} as {
        enableGrouping?: boolean
        GroupedCell?: (props: any) => JSX.Element
    }
    let canGroupColumns = false
    const columnGroupingValue = buildGetGroupingValue(column)

    // For big sheets, we are disabling the aggregation and grouping. See more in LEG-2356.
    if (canFitInMemory) {
        canGroupColumns = true

        columnGroupingData = {
            enableGrouping: true,
            GroupedCell: (props) => {
                return (
                    <MantineGroupedSheetCell
                        isMantineSheet
                        id={id}
                        column={columnsToRender.find((col) => col.columnId === column.id)}
                        columnIndex={columnIndex}
                        datum={fromJS({})}
                        dataType={dataType}
                        isGrouped
                        cellValue={props.column.accessorFn(props.row)}
                        groupedAmount={props.row.leafRows.length}
                    />
                )
            },
        }
        if (isStatColumn(column.column_data_type, column.field, column.column_type)) {
            columnAggregationData = {
                // NOTE: Tanstack Table accepts string names of Table-defined aggregation functions, but MRT doesn't
                // recognize them as valid in its typing
                aggregationFn: "numberSum" as MRT_AggregationOption,
                AggregatedCell: (props) => {
                    const cellValue = props.cell.getValue()
                    const groupedColumn = props.table.getColumn(props.cell.row.groupingColumnId ?? "")
                    const aggregationLabel = `Sum by ${groupedColumn?.columnDef.header}:`
                    const aggregatedValue =
                        column.column_data_type ===
                        DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.currency.value
                            ? centsToCurrencyStr(cellValue)
                            : new Intl.NumberFormat().format(cellValue)
                    return <MantineAggregationCell label={aggregationLabel} value={aggregatedValue} />
                },
            }
        }
    }

    const isNumeric = [
        DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value,
        DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.decimal.value,
        DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.long.value,
        DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.percent.value,
        DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.year.value,
        DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.currency.value,
    ].includes(columnMeta.columnDataType)

    const columnDef: MRT_ColumnDef = {
        // NOTE: columnFilterData can override accessorFn for certain column types to
        // support proper filtering; if you need to define a specific accessorFn, be sure
        // that it isn't being overridden there
        accessorFn: (row) => column.data && column.data[getSafedSearchRowId(row)],
        enableSorting: true,
        header: column.name,
        id: column.id,
        maxSize: sheetConstants.COLUMN_MAX_WIDTH,
        minSize: sheetConstants.MANTINE_COLUMN_MIN_WIDTH,
        renderColumnActionsMenuItems: ({ column: columnForMenu, table }) => (
            <ColumnActionMenu
                columnDefinition={columnForMenu}
                canAddColumns={canAddColumns}
                canHideColumns={canHideColumns}
                canRemoveColumns={canRemoveColumns}
                canRenameColumns={canRenameColumns}
                isDatumColumn={false}
                sheetId={sheetId}
                columnIndex={columnIndex}
                onRemoveColumn={() => removeColumn(id, column.id, columnIndex)}
                columnId={column.id}
                toggleColumnIsHiddenOnPublic={() =>
                    toggleColumnIsHiddenOnPublic(id, column.id, !column.should_hide_column_on_public)
                }
                isHidden={column.should_hide_column_on_public}
                onRenameColumn={() => handleRenameColumn(id, column, renameColumn)}
                canGroupColumns={canGroupColumns}
                canColorColumns={isColumnColoringEnabled(column, dataType)}
                table={table}
                isPinned={isPinned}
                onPinSort={togglePinnedSort}
            />
        ),
        ...globalSearchProps,
        size: column.width || sheetConstants.DEFAULT_COLUMN_WIDTH,
        // Per Cell (Switch statement)
        ...columnFilterData,
        ...columnSortData,
        ...columnAggregationData,
        ...columnGroupingData,
        ...columnGroupingValue,
        meta: columnMeta,
        Cell: (props) => {
            const cellValue = column?.data?.[getSafedSearchRowId(props.row)] || props.row.getValue(column.id)
            return (
                <MantineSheetCell
                    isMantineSheet
                    rowIndex={props.row.index}
                    rowId={getSafedSearchRowId(props.row)}
                    id={id}
                    columnId={column.id}
                    column={columnsToRender.find((col) => col.columnId === column.id)}
                    columnCount={columnCount}
                    columnIndex={columnIndex}
                    columnDataType={column.column_data_type}
                    datum={fromJS(props.row.original)}
                    dataType={dataType}
                    // redundant but directly replacing exact selector output
                    sheetDataType={dataType}
                    row={props.row}
                    width={column.width}
                    // the direct value to render in this cell -- previously from `selectCellValue
                    // but that behaves weirdly with redux state architecture.
                    // NOTE: we need to do this more delicately and migrate from redux
                    // hollistically, not with one-off changes.
                    cellValue={cellValue?.toJS?.() ?? cellValue}
                    isNumeric={isNumeric}
                />
            )
        },
        Header: ({ column }) => {
            const { classes } = useStyles()

            return (
                <div className="header-text">
                    {isPinned && (
                        <Button
                            className={classes.pinnedIcon}
                            variant="light"
                            size="xs"
                            tabIndex={-1}
                            aria-hidden="true"
                        >
                            <Icon icon="thumbtack" size="xs" />
                        </Button>
                    )}
                    {column.columnDef.header}
                </div>
            )
        },
        Footer:
            shouldDisplayMathBar &&
            (() => (
                <MantineAggregationCell
                    label={"Sum:"}
                    value={getMathFooterLabel(column, columns, rowData.toArray(), dataType)}
                />
            )),
    }

    return columnDef
}
